import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { onSnapshot, orderBy, query } from "firebase/firestore";
import { auth } from "../../firebase";
import Window from "../../components/Window";
import Icon from "../../components/Icon";
import ContentWarning from "../../components/ContentWarning";
import RoomPostForm from "../../components/RoomPostForm";
import { SafetyActions } from "../../components/SafetyBits";
import { sanitizeHtml } from "../../sanitize";
import { ReportTarget, sendKudos } from "../../penpal";
import {
  Post,
  Thread,
  isMine,
  markSeen,
  removeReply,
  removeThread,
  repliesCol,
  replyTo,
  roomById,
  threadRef,
} from "../../rooms";
import {
  Circle,
  circlePostRef,
  circleRepliesCol,
  isLead,
  removeCirclePost,
  removeCircleReply,
  replyInCircle,
  setCirclePostHidden,
  setCircleReplyHidden,
  useCircle,
  useMembership,
} from "../../circles";
import { useBlocks } from "../../blocks";
import {
  Piece,
  commentOnPiece,
  deliveryName,
  pieceRef,
  pieceRepliesCol,
  postmark,
  takeBackPiece,
  takeBackPieceComment,
} from "../../post";
import "../../styles.css";

const when = (t?: { toDate: () => Date }) =>
  t
    ? t.toDate().toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";

// Where a post lives: a community room, a private circle (where its
// quartermaster and first mates can hide posts and comments), or the
// PenPal Post (where pieces are submitted, then delivered by an editor).
type Place = { kind: "room" } | { kind: "circle"; circle: Circle; lead: boolean } | { kind: "post" };

// A room/circle post, or a PenPal Post piece (which adds these fields).
type AnyPost = Thread & Partial<Pick<Piece, "kind" | "category" | "status" | "editorNote" | "deliveredAt">>;

// Everything that differs between places.
function placeTools(place: Place, postId: string) {
  if (place.kind === "post") {
    return {
      postRef: pieceRef(postId),
      repliesRef: pieceRepliesCol(postId),
      key: (rid?: string) => (rid ? `pp_${postId}_${rid}` : `pp_${postId}`),
      reply: (uid: string, username: string, body: string, cw: string, anon: boolean) =>
        commentOnPiece(uid, username, postId, body, cw, anon),
      removePost: () => takeBackPiece(postId),
      removeReply: (rid: string) => takeBackPieceComment(postId, rid),
      setHidden: undefined as undefined | ((rid: string | undefined, hidden: boolean) => Promise<void>),
      report: { pieceId: postId } as Partial<ReportTarget>,
    };
  }
  if (place.kind === "room") {
    return {
      postRef: threadRef(postId),
      repliesRef: repliesCol(postId),
      key: (rid?: string) => (rid ? `${postId}_${rid}` : postId),
      reply: (uid: string, username: string, body: string, cw: string, anon: boolean) =>
        replyTo(uid, username, postId, body, cw, anon),
      removePost: () => removeThread(postId),
      removeReply: (rid: string) => removeReply(postId, rid),
      setHidden: undefined as undefined | ((rid: string | undefined, hidden: boolean) => Promise<void>),
      report: {} as Partial<ReportTarget>,
    };
  }
  const cid = place.circle.id;
  return {
    postRef: circlePostRef(cid, postId),
    repliesRef: circleRepliesCol(cid, postId),
    key: (rid?: string) => (rid ? `${cid}_${postId}_${rid}` : `${cid}_${postId}`),
    reply: (uid: string, username: string, body: string, cw: string, anon: boolean) =>
      replyInCircle(uid, username, cid, postId, body, cw, anon),
    removePost: () => removeCirclePost(cid, postId),
    removeReply: (rid: string) => removeCircleReply(cid, postId, rid),
    setHidden: place.lead
      ? (rid: string | undefined, hidden: boolean) =>
          rid ? setCircleReplyHidden(cid, postId, rid, hidden) : setCirclePostHidden(cid, postId, hidden)
      : undefined,
    report: { circleId: cid } as Partial<ReportTarget>,
  };
}

// /community/thread/:threadId
export default function ThreadPage() {
  const { threadId = "" } = useParams();
  return <PostThread place={{ kind: "room" }} postId={threadId} />;
}

// /post/:pieceId (a PenPal Post piece)
export function PiecePage() {
  const { pieceId = "" } = useParams();
  return <PostThread place={{ kind: "post" }} postId={pieceId} />;
}

// /circles/:circleId/post/:postId (members only)
export function CirclePostPage() {
  const { circleId = "", postId = "" } = useParams();
  const [user] = useAuthState(auth);
  const c = useCircle(circleId);
  const member = useMembership(circleId, user?.uid);
  if (c === undefined || member === undefined) return <p className="empty">Opening the post...</p>;
  if (!c || !member) {
    return (
      <div className="window">
        <p className="empty">
          Only members of this circle can read its posts. <Link to={`/circles/${circleId}`}>Go to the circle</Link>
        </p>
      </div>
    );
  }
  return <PostThread place={{ kind: "circle", circle: c, lead: isLead(c, user?.uid) }} postId={postId} />;
}

// A post, laid out like a board letter, with comments underneath.
function PostThread({ place, postId }: { place: Place; postId: string }) {
  const [user] = useAuthState(auth);
  const { ids: blocked } = useBlocks();
  const [post, setPost] = useState<AnyPost | null | undefined>(undefined);
  const [comments, setComments] = useState<Post[]>([]);
  const [mine, setMine] = useState(false);
  const tools = placeTools(place, postId);
  const { postRef, repliesRef } = tools;
  const backTo =
    place.kind === "post"
      ? "/post"
      : place.kind === "circle"
      ? `/circles/${place.circle.id}`
      : post?.room
      ? `/community/${post.room}`
      : "/community";
  const backLabel =
    place.kind === "post"
      ? "The PenPal Post"
      : place.kind === "circle"
      ? place.circle.name
      : roomById(post?.room)?.title || "All rooms";

  useEffect(
    () =>
      onSnapshot(
        postRef,
        (snap) => setPost(snap.exists() ? ({ id: snap.id, ...snap.data() } as AnyPost) : null),
        () => setPost(null)
      ),
    [postRef.path]
  );
  useEffect(
    () =>
      onSnapshot(
        query(repliesRef, orderBy("createdAt", "asc")),
        (snap) => setComments(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Post, "id">) }))),
        () => setComments([])
      ),
    [repliesRef.path]
  );
  // Seeing a room post clears its "new comments" badge.
  useEffect(() => {
    if (post && place.kind === "room") markSeen(postId);
  }, [post, comments.length, postId, place.kind]);
  useEffect(() => {
    if (user && post && !post.removed) isMine(user.uid, post, tools.key()).then(setMine);
  }, [user, post]);

  if (post === undefined) return <p className="empty">Opening the post...</p>;
  if (post === null) {
    return (
      <div className="window">
        <p className="empty">
          This post isn't here anymore. <Link to={backTo}>Go back</Link>
        </p>
      </div>
    );
  }
  const room = place.kind === "room" ? roomById(post.room) : undefined;
  const hiddenFromMe = !!post.uid && blocked.has(post.uid);
  const takeBack = async () => {
    if (!window.confirm("Take this post back? It will be removed for everyone.")) return;
    await tools.removePost().catch(() => alert("Sorry, that didn't work. Please try again."));
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={backTo} className="btn btn-plain btn-sm">
          <Icon name="back" size={16} /> {backLabel}
        </Link>
      </div>

      {place.kind === "post" && post.status !== "delivered" && (
        <p className="notice text-sm" role="status">
          {post.status === "returned" ? (
            <>
              <strong>Returned to sender.</strong> Only you and the editors can see this piece.
              {post.editorNote && <span className="block mt-1">Editor's note: "{post.editorNote}"</span>}
            </>
          ) : (
            <>
              <strong>Waiting to be read.</strong> A real person reads everything before it's
              published, so this can take a day or two. Until then, only you and the editors
              can see it.
            </>
          )}
        </p>
      )}

      <Window title="letter.txt" tone="tan">
        {place.kind === "post" && post.deliveredAt && (
          <p className="postmark" aria-label={`Delivered ${postmark(post.deliveredAt)}`}>
            PENPAL POST · {postmark(post.deliveredAt)}
          </p>
        )}
        <p className="mb-1 text-sm text-ink-soft">
          <Icon name="pin" size={14} />{" "}
          {post.kind === "dispatch" ? (
            <>From the Post Office (@{post.username})</>
          ) : place.kind === "post" && post.anonymous ? (
            <>Written anonymously</>
          ) : place.kind === "post" ? (
            <>
              Written by <Link to={`/profile/${post.uid}`}>@{post.username}</Link>
            </>
          ) : post.anonymous ? (
            <>Posted anonymously</>
          ) : (
            <>
              Posted by <Link to={`/profile/${post.uid}`}>@{post.username}</Link>
            </>
          )}
          {room && ` in ${room.title}`}
          {place.kind === "circle" && ` in ${place.circle.name}`}
          {post.category && ` · ${post.category}`}
          {place.kind === "post"
            ? post.deliveredAt && ` · ${deliveryName(post.deliveredAt)} delivery`
            : post.createdAt && ` on ${when(post.createdAt)}`}
          {mine && post.anonymous && " · (you, anonymously)"}
        </p>
        <h1 className="window-heading mb-4">{post.title}</h1>

        {post.removed ? (
          <p className="text-sm italic text-ink-soft">(The writer took this post back.)</p>
        ) : hiddenFromMe ? (
          <p className="text-sm italic text-ink-soft">(Hidden: you blocked this person.)</p>
        ) : post.hidden && !tools.setHidden ? (
          <p className="text-sm italic text-ink-soft">(Hidden by the circle's quartermaster or a first mate.)</p>
        ) : (
          <>
            {post.hidden && (
              <p className="notice mb-3 text-sm">Hidden from members. Only leads can see it.</p>
            )}
            <ContentWarning cw={post.cw}>
              <div
                className="lined-paper"
                style={post.color ? { background: post.color } : undefined}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.body) }}
              />
            </ContentWarning>

            {post.categories && post.categories.length > 0 && (
              <div className="chip-row mt-4">
                {post.categories.map((cat) => (
                  <span key={cat} className="chip">
                    {cat}
                  </span>
                ))}
              </div>
            )}

            <div className="divider my-5" />
            <div className="btn-row justify-center">
              {mine ? (
                <button onClick={takeBack} className="btn btn-plain btn-sm">
                  <Icon name="close" size={14} /> Take back my post
                </button>
              ) : (
                post.uid && <KudosButton toUid={post.uid} note={`for your post "${post.title}"`} />
              )}
              {tools.setHidden && (
                <button
                  type="button"
                  className="btn btn-plain btn-sm"
                  onClick={() => tools.setHidden!(undefined, !post.hidden).catch(() => alert("That didn't work."))}
                >
                  <Icon name="eye" size={14} /> {post.hidden ? "Unhide" : "Hide from members"}
                </button>
              )}
            </div>
            {!mine && (
              <div className="mt-2 text-center">
                <SafetyActions
                  target={{
                    threadId: postId,
                    ...tools.report,
                    ...(post.uid ? { userId: post.uid, username: post.username } : {}),
                  }}
                  canBlock={!!post.uid}
                />
              </div>
            )}
          </>
        )}
      </Window>

      <Window title={`comments (${comments.filter((c) => !c.removed && !c.hidden).length})`} tone="mint">
        {comments.length === 0 ? (
          <p className="text-sm text-ink-soft">No comments yet. Be the first to say something kind.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {comments.map((c) => (
              <li key={c.id} className="profile-box bg-white">
                <div className="profile-box-body">
                  <Comment
                    comment={c}
                    postId={postId}
                    postTitle={post.title}
                    tools={tools}
                    blocked={!!c.uid && blocked.has(c.uid)}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}

        {user && !post.removed && (place.kind !== "post" || post.status === "delivered") && (
          <>
            <div className="divider my-4" />
            <h2 className="font-display text-lg font-semibold">Leave a comment</h2>
            <RoomPostForm
              withTitle={false}
              submitLabel="Comment"
              onSubmit={(p) => tools.reply(user.uid, p.username, p.body, p.cw, p.anonymous)}
            />
          </>
        )}
      </Window>
    </>
  );
}

// One comment: who, when, the words (behind a content warning if any),
// kudos, Report, Take back for your own, and Hide for circle leads.
function Comment({
  comment,
  postId,
  postTitle,
  tools,
  blocked,
}: {
  comment: Post;
  postId: string;
  postTitle: string;
  tools: ReturnType<typeof placeTools>;
  blocked: boolean;
}) {
  const [user] = useAuthState(auth);
  const [mine, setMine] = useState(false);
  const key = tools.key(comment.id);

  useEffect(() => {
    if (user && !comment.removed) isMine(user.uid, comment, key).then(setMine);
  }, [user, comment, key]);

  if (comment.removed) return <p className="text-sm italic text-ink-soft">(The writer took this comment back.)</p>;
  if (blocked) return <p className="text-sm italic text-ink-soft">(Hidden: you blocked this person.)</p>;
  if (comment.hidden && !tools.setHidden) {
    return <p className="text-sm italic text-ink-soft">(Hidden by the circle's quartermaster or a first mate.)</p>;
  }

  const takeBack = async () => {
    if (!window.confirm("Take this comment back? It will be removed for everyone.")) return;
    await tools.removeReply(comment.id).catch(() => alert("Sorry, that didn't work. Please try again."));
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-ink-soft">
        {comment.anonymous ? "anonymous" : <Link to={`/profile/${comment.uid}`}>@{comment.username}</Link>} ·{" "}
        {when(comment.createdAt)}
        {mine && comment.anonymous && " · (you, anonymously)"}
        {comment.hidden && " · hidden from members"}
      </p>
      <ContentWarning cw={comment.cw}>
        <p className="whitespace-pre-wrap">{comment.body}</p>
      </ContentWarning>
      <div className="flex flex-wrap items-center gap-3">
        {mine ? (
          <button type="button" className="text-sm underline" onClick={takeBack}>
            Take back
          </button>
        ) : (
          <>
            {comment.uid && <KudosButton toUid={comment.uid} note={`for your comment on "${postTitle}"`} small />}
            <SafetyActions
              target={{
                threadId: postId,
                replyId: comment.id,
                ...tools.report,
                ...(comment.uid ? { userId: comment.uid, username: comment.username } : {}),
              }}
              canBlock={!!comment.uid}
            />
          </>
        )}
        {tools.setHidden && (
          <button
            type="button"
            className="text-sm underline"
            onClick={() => tools.setHidden!(comment.id, !comment.hidden).catch(() => alert("That didn't work."))}
          >
            {comment.hidden ? "Unhide" : "Hide"}
          </button>
        )}
      </div>
    </div>
  );
}

// Send kudos to the writer. They land in their inbox, saying what they were for.
function KudosButton({ toUid, note, small }: { toUid: string; note: string; small?: boolean }) {
  const [user] = useAuthState(auth);
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  if (!user || user.uid === toUid) return null;

  const send = async () => {
    setState("sending");
    const ok = await sendKudos(user.uid, toUid, note.slice(0, 150));
    setState(ok === null ? "idle" : ok ? "sent" : "failed");
  };

  if (state === "sent") return <span className="success-text text-sm">Kudos sent! ✿</span>;
  return (
    <button
      type="button"
      className={small ? "chip" : "btn btn-plain"}
      onClick={send}
      disabled={state === "sending"}
      title={state === "failed" ? "That didn't send. Try again?" : undefined}
    >
      <Icon name="gift" size={small ? 12 : 16} /> {state === "failed" ? "Try again" : "Send kudos"}
    </button>
  );
}
